// 认证上下文：AuthProvider 探测会话（/api/auth/me）、缓存 CSRF token、
// 登录跳转（飞书 OAuth url）、登出；RequireAuth / RequireCapability 路由守卫。

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Spinner } from "@bf/ui";
import type { Capability } from "../../shared/capabilities";
import type { LarkAuthUrlResponse, MeResponse, UserProfile } from "../../shared/contracts";
import { api, setCsrfToken } from "./api";

interface AuthState {
  user: UserProfile | null;
  /** 首次 me 探测进行中 */
  loading: boolean;
  capabilities: Capability[];
  hasCapability: (...caps: Capability[]) => boolean;
  /** 跳飞书登录（扫码/免登同一 OAuth 流程） */
  login: (redirect?: string) => Promise<void>;
  logout: () => Promise<void>;
  /** 重新拉取 me（登录回调、角色变更后调用） */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await api.get<MeResponse>("/api/auth/me", { skipAuthRedirect: true });
      setCsrfToken(me.csrfToken);
      setUser(me.user);
    } catch {
      setCsrfToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (redirect?: string) => {
    const target = redirect ?? window.location.pathname + window.location.search;
    const { url } = await api.get<LarkAuthUrlResponse>("/api/auth/lark/url", {
      query: { redirect: target },
      skipAuthRedirect: true,
    });
    window.location.assign(url);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } finally {
      setCsrfToken(null);
      setUser(null);
      window.location.assign("/login");
    }
  }, []);

  const value = useMemo<AuthState>(() => {
    const capabilities = user?.capabilities ?? [];
    return {
      user,
      loading,
      capabilities,
      hasCapability: (...caps) => caps.some((cap) => capabilities.includes(cap)),
      login,
      logout,
      refresh,
    };
  }, [user, loading, login, logout, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth 必须在 AuthProvider 内使用");
  return ctx;
}

/** 会话探测中的整屏加载态（避免闪烁到登录页） */
function AuthLoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f5f6f7]">
      <div className="flex items-center gap-2 text-sm text-gray-400">
        <Spinner size="sm" borderClassName="border-gray-300 border-t-brand" />
        加载中…
      </div>
    </div>
  );
}

/** 路由守卫：未登录 → /login（带 redirect 回跳参数） */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <AuthLoadingScreen />;
  if (!user) {
    const redirect = location.pathname + location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirect)}`} replace />;
  }
  return <>{children}</>;
}

/** 路由守卫：任一能力命中即放行，否则 → /403。须放在 RequireAuth 内。 */
export function RequireCapability({ anyOf, children }: { anyOf: Capability[]; children: ReactNode }) {
  const { hasCapability } = useAuth();
  if (!hasCapability(...anyOf)) return <Navigate to="/403" replace />;
  return <>{children}</>;
}
